'use client'

import { parseCookies } from 'nookies'
import type { ReactElement } from 'react'
import { useCallback, useEffect, useState } from 'react'

import type { IFirebaseToken } from 'src/firebase/hooks/use-fcm-token'
import useFcmToken, { FIREBASE_REQUEST_EXPLAIN } from 'src/firebase/hooks/use-fcm-token'
import { mappingCurrentTenantCookie } from '~/cookies/currentTenant'
import type { FCC, IRole, ITenantDetail } from '~/core/@types/global'
import {
  SESSION_COOKIE_CHUB_USER,
  SESSION_COOKIE_CURRENT_TENANT,
  SESSION_COOKIE_CURRENT_TENANT_EXT,
  SESSION_COOKIE_USER,
  SESSION_EXPIRES_AT_COOKIE_NAME
} from '~/core/constants/cookies'
import { PAGES_NOT_FETCH_API } from '~/core/constants/url'
import {
  setSessionCookieClient,
  setSessionCookieExtClient
} from '~/core/middleware/save-session-cookie'
import useContextGraphQL from '~/core/middleware/use-context-graphql'
import { catchErrorFromGraphQL } from '~/core/utilities/catch-api-error'

import { useRouter } from 'next/navigation'
import { mappingUserCookie } from '~/cookies/user'
import type { ILogoAndAvatarVariants, IUserInformation } from '~/core/@types/global'
import QueryNotificationsUnreadCount from '~/lib/features/notifications/graphql/query-notifications-unread-count'
import useNotificationStore from '~/lib/features/notifications/store'
import QueryUserPermissionsList from '~/lib/features/permissions/graphql/query-user-permissions-list'
import QueryCareersSetting from '~/lib/features/settings/careers/graphql/query-careers-setting'
import type { CareerSettingType } from '~/lib/features/settings/careers/types'
import QueryShowTenantMember from '~/lib/features/settings/members/graphql/query-show-member'
import useWorkSpace from '~/lib/features/settings/workspace/hooks/use-workspace'
import QueryTenantFeatureSetting from '~/lib/graphql/query-tenant-feature-setting'
import { useRouterContext } from '~/lib/next/use-router-context'
import useBoundStore from '~/lib/store'
import type { ITenantPermissionSettingType } from '~/lib/store/permission-setting-slice'
import useToastStore from '~/lib/store/toast'
import { isCareerHubApp } from '~/lib/utilities/pathname'

import { revalidateAfterCookieSet } from '~/core/middleware/revalidate-path-cookie'
import { refetchSubscriptionData } from '../Subscription/use-subscription-plan'

const PermissionWrapper: FCC<{ children: ReactElement<any> }> = ({ children }) => {
  const router = useRouter()
  const { pathnameWithoutLocale } = useRouterContext()
  const { clientGraphQL } = useContextGraphQL()
  const {
    setPermissionSetting,
    user,
    currentRole,
    setCurrentRole,
    setDeviceRegistrations,
    setFeatureSetting,
    setUser
  } = useBoundStore()
  const { setToast } = useToastStore()
  const { setNotificationTotalUnreadCount } = useNotificationStore()
  const { subscribeFCM } = useFcmToken()
  const cookies = parseCookies()
  const [isFirstLoaded, setFirstLoaded] = useState(true)
  const { tenantShow: dataCompany, fetchWorkspace } = useWorkSpace({
    shouldPause: true
  })

  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      if (user?.id && isFirstLoaded === true) {
        subscribeFCM((payload: { data: { title: string } }) => {
          const isCareerHub = isCareerHubApp(window.location.pathname)
          if (isCareerHub) {
            return
          }
          if (payload.data.title === FIREBASE_REQUEST_EXPLAIN.userPermissionsListChanged.title) {
            fetchPermissions()
          }

          if (payload.data.title === FIREBASE_REQUEST_EXPLAIN.PlanSubscriptionChanged.title) {
            refetchSubscriptionData(clientGraphQL)
          }

          if (payload.data.title === FIREBASE_REQUEST_EXPLAIN.tenantReferralSettingChanged.title) {
            fetchTenantFeatureSetting()
          }

          if (
            payload.data.title === FIREBASE_REQUEST_EXPLAIN.tenantRequisitionSettingChanged.title
          ) {
            fetchTenantFeatureSetting()
          }

          if (
            payload.data.title === FIREBASE_REQUEST_EXPLAIN.tenantCareerSiteSettingChanged.title
          ) {
            fetchTenantSettingCareerSite()
          }

          if (payload.data.title === FIREBASE_REQUEST_EXPLAIN.userHasNewNotification.title) {
            fetchNotificationBadgeCount()
          }
        })
      }
    }

    return
  }, [user])

  // Internal
  useEffect(() => {
    const isCareerHubAccessDenied =
      pathnameWithoutLocale?.includes('/careerhub/') ||
      pathnameWithoutLocale?.endsWith('/access-denied')
    const shouldSkipApiFetch = Array.from(PAGES_NOT_FETCH_API).some((url) =>
      pathnameWithoutLocale.startsWith(url)
    )

    if (
      isCareerHubAccessDenied ||
      shouldSkipApiFetch ||
      !user?.id ||
      !isFirstLoaded ||
      (user?.tenants?.data || []).length === 0
    ) {
      return
    }
    setFirstLoaded(false)
    fetchPermissions()
    fetchTenantFeatureSetting()
    fetchWorkspace()
    if (currentRole === undefined) {
      fetchInformationUsers()
    }
  }, [user?.id, pathnameWithoutLocale])

  // CareerHub
  useEffect(() => {
    const isCareerHubIgnoreFetchAPI =
      pathnameWithoutLocale?.includes('/welcome') ||
      pathnameWithoutLocale?.endsWith('/access-denied')
    const shouldSkipApiFetch = Array.from(PAGES_NOT_FETCH_API).some((url) =>
      pathnameWithoutLocale.startsWith(url)
    )
    if (
      !pathnameWithoutLocale?.includes('/careerhub/') ||
      isCareerHubIgnoreFetchAPI ||
      shouldSkipApiFetch ||
      !user?.id ||
      !isFirstLoaded ||
      currentRole !== undefined
    ) {
      return
    }
    fetchInformationUsers()
  }, [user?.id, pathnameWithoutLocale, isFirstLoaded])

  useEffect(() => {
    if (dataCompany && Object.keys(dataCompany).length > 0) {
      if (user.currentTenant) {
        const tenantFromOldCookie = JSON.parse(cookies[SESSION_COOKIE_CURRENT_TENANT] || '{}') || {}
        const obj = {
          ...tenantFromOldCookie,
          ...user.currentTenant,
          ...dataCompany,
          careerSiteSettings: dataCompany.careerSiteSettings
        }
        const currentTenantObject = mappingCurrentTenantCookie(obj)

        setSessionCookieClient(
          SESSION_COOKIE_CURRENT_TENANT,
          JSON.stringify(currentTenantObject).toString(),
          Number(cookies[SESSION_EXPIRES_AT_COOKIE_NAME])
        )
        setSessionCookieExtClient(
          SESSION_COOKIE_CURRENT_TENANT_EXT,
          JSON.stringify(currentTenantObject).toString(),
          Number(cookies[SESSION_EXPIRES_AT_COOKIE_NAME])
        )

        setUser({
          ...user,
          currentTenant: currentTenantObject as unknown as ITenantDetail
        })
      }
    }
  }, [dataCompany])

  const fetchPermissions = useCallback(() => {
    return clientGraphQL.query(QueryUserPermissionsList, { userId: Number(user.id) }).then(
      (result: {
        error: { graphQLErrors: Array<object> }
        data: {
          userPermissionsList: {
            collection: ITenantPermissionSettingType[]
          }
        }
      }) => {
        if (result.error) {
          return catchErrorFromGraphQL({
            error: result.error,
            setToast
          })
        }

        const { userPermissionsList } = result.data || {}

        return setPermissionSetting(userPermissionsList?.collection || [])
      }
    )
  }, [clientGraphQL, user])

  const fetchTenantFeatureSetting = useCallback(() => {
    return clientGraphQL.query(QueryTenantFeatureSetting).then(
      (result: {
        error: { graphQLErrors: Array<object> }
        data: {
          tenantFeatureSetting: {
            key: string
            enabling_feature: boolean
          }[]
        }
      }) => {
        if (result.error) {
          return catchErrorFromGraphQL({
            error: result.error,
            setToast
          })
        }

        const { tenantFeatureSetting } = result.data || {}
        return setFeatureSetting(tenantFeatureSetting)
      }
    )
  }, [clientGraphQL, user])

  const fetchInformationUsers = () => {
    clientGraphQL.query(QueryShowTenantMember, { id: Number(user.id) }).then(
      (result: {
        error: { graphQLErrors: Array<object> }
        data: {
          showTenantMember: {
            deviceRegistrations: IFirebaseToken[]
            fullName: string
            phoneNumber: string
            jobTitle: string
            language: string
            timezone: string
            avatarVariants: ILogoAndAvatarVariants
            defaultColour: string
            roles: IRole[]
            chubUserTenants?: Array<{
              consentPolicies?: Array<{
                agreedAt: string
                createdAt: string
                status: string
                statusDescription: string
              }>
            }>
          }
        }
      }) => {
        if (result.error) {
          return catchErrorFromGraphQL({
            error: result.error,
            setToast
          })
        }

        const resultUser = result?.data?.showTenantMember || {}
        const userCookieKey = isCareerHubApp(window.location.pathname)
          ? SESSION_COOKIE_CHUB_USER
          : SESSION_COOKIE_USER
        const useFromCookie = cookies?.[userCookieKey]
          ? JSON.parse(cookies[userCookieKey] || '{}')
          : {}

        const newUserInfo = mappingUserCookie({
          ...useFromCookie,
          ...resultUser,
          // apply structure chubUserTenants for user
          chubUserTenants: {
            ...(useFromCookie?.chubUserTenants || {}),
            data: [
              ...(useFromCookie?.chubUserTenants?.data || [
                {
                  attributes: {
                    consentPolicies: []
                  }
                }
              ])
            ]
          },
          onboarding:
            (resultUser as unknown as IUserInformation)?.onboarding ??
            useFromCookie?.onboarding ??
            false
        } as unknown as IUserInformation)

        // get data consentPolicies from QueryShowTenantMember
        if (newUserInfo?.chubUserTenants?.data?.[0]?.attributes) {
          newUserInfo.chubUserTenants.data[0].attributes.consentPolicies =
            resultUser?.chubUserTenants?.[0]?.consentPolicies
        }

        setSessionCookieClient(
          userCookieKey,
          JSON.stringify(newUserInfo).toString(),
          Number(cookies[SESSION_EXPIRES_AT_COOKIE_NAME])
        )

        setTimeout(() => {
          revalidateAfterCookieSet('/')
          setUser({
            ...user,
            fullName: newUserInfo.fullName,
            phoneNumber: newUserInfo.phoneNumber,
            jobTitle: newUserInfo.jobTitle,
            language: newUserInfo.language,
            timezone: newUserInfo.timezone,
            avatarVariants: newUserInfo?.avatarVariants,
            defaultColour: newUserInfo.defaultColour,
            chubUserTenants: newUserInfo?.chubUserTenants
          })
          if (user?.language !== newUserInfo?.language) {
            router.refresh()
          }
        }, 0)

        const { showTenantMember } = result.data || {}
        setCurrentRole(showTenantMember?.roles?.[0] as IRole)
        setDeviceRegistrations(showTenantMember?.deviceRegistrations || [])

        return
      }
    )
  }

  const fetchNotificationBadgeCount = useCallback(() => {
    clientGraphQL.query(QueryNotificationsUnreadCount).then(
      (result: {
        error: { graphQLErrors: Array<object> }
        data: {
          notificationsUnreadCount: number
        }
      }) => {
        if (result.error) {
          return catchErrorFromGraphQL({
            error: result.error,
            setToast
          })
        }

        const { notificationsUnreadCount } = result.data || {}
        const count = notificationsUnreadCount >= 0 ? notificationsUnreadCount : 0
        setNotificationTotalUnreadCount(count)

        return
      }
    )
  }, [clientGraphQL])

  const fetchTenantSettingCareerSite = useCallback(async () => {
    return clientGraphQL
      .query(QueryCareersSetting, {})
      .toPromise()
      .then(
        (result: {
          error: { graphQLErrors: object[] }
          data: { tenantSettingsCareerSite: CareerSettingType }
        }) => {
          if (result.error) {
            return catchErrorFromGraphQL({
              error: result.error,
              setToast
            })
          }

          const { tenantSettingsCareerSite } = result.data
          if (user?.currentTenant) {
            const tenantFromOldCookie =
              JSON.parse(cookies[SESSION_COOKIE_CURRENT_TENANT] || '{}') || {}
            const obj = {
              ...tenantFromOldCookie,
              ...user.currentTenant,
              careerSiteSettings: tenantSettingsCareerSite
            }

            const currentTenantObject = mappingCurrentTenantCookie(obj)

            setSessionCookieClient(
              SESSION_COOKIE_CURRENT_TENANT,
              JSON.stringify(currentTenantObject).toString(),
              Number(cookies[SESSION_EXPIRES_AT_COOKIE_NAME])
            )
            setSessionCookieExtClient(
              SESSION_COOKIE_CURRENT_TENANT_EXT,
              JSON.stringify(currentTenantObject).toString(),
              Number(cookies[SESSION_EXPIRES_AT_COOKIE_NAME])
            )

            setUser({
              ...user,
              currentTenant: {
                ...user?.currentTenant,
                careerSiteSettings: {
                  ...user?.currentTenant?.careerSiteSettings,
                  enablingCareerSiteSetting: tenantSettingsCareerSite?.enablingCareerSiteSetting
                }
              } as ITenantDetail
            })
          }

          return
        }
      )
  }, [clientGraphQL, user])

  return children
}

export default PermissionWrapper
